import { Component } from "react";
import ErrorPage from "../../pages/ErrorPage";
import { trackEvent } from "../../utils/tracking";

const RELOAD_FLAG = "aw_chunk_reload";

function isStaleChunkError(error) {
    return (
        error?.name === "ChunkLoadError" ||
        /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || "")
    );
}

/**
 * Catches render/runtime errors and shows the branded error page.
 *
 * Special case: after a deploy, users holding the old index.html hit
 * ChunkLoadError when lazy routes request chunks that no longer exist.
 * That is fixed by a reload, so we do it automatically — once per
 * session, guarded by sessionStorage to avoid reload loops.
 */
export default class ErrorBoundary extends Component {
    state = { error: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        if (isStaleChunkError(error)) {
            let alreadyReloaded = false;
            try {
                alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === "1";
                if (!alreadyReloaded) sessionStorage.setItem(RELOAD_FLAG, "1");
            } catch (e) {
                // storage unavailable: fall through to the error page
                alreadyReloaded = true;
            }
            if (!alreadyReloaded) {
                window.location.reload();
                return;
            }
        }

        trackEvent("app_error", {
            error_message: String(error?.message || error).slice(0, 150),
            error_stale_chunk: isStaleChunkError(error),
        });
        // eslint-disable-next-line no-console
        console.error("ErrorBoundary caught:", error, info?.componentStack);
    }

    handleRetry = () => {
        try {
            sessionStorage.removeItem(RELOAD_FLAG);
        } catch (e) {
            // ignore
        }
        window.location.reload();
    };

    render() {
        if (this.state.error) {
            return <ErrorPage onRetry={this.handleRetry} />;
        }
        return this.props.children;
    }
}
